import React, { useState, useEffect, useCallback } from 'react';
import { X, AlertCircle } from 'lucide-react';
import api from '../utils/api';
import SkuComparisonLoader from './SkuComparisonLoader';
import PriceTimeline from './PriceTimeline';

interface SkuTrendCompareProps {
  tableName: string;
}

const MAX_SKUS = 2;

interface LoadedSku {
  sku: string;
  products: any[];
}

const SkuTrendCompare: React.FC<SkuTrendCompareProps> = ({ tableName }) => {
  const [loadedSkus, setLoadedSkus] = useState<LoadedSku[]>([]);
  const [priceHistoryData, setPriceHistoryData] = useState<any>(null);
  const [loadingPriceHistory, setLoadingPriceHistory] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  // Productos planos derivados de los SKUs cargados (para PriceTimeline)
  const comparisonProducts = React.useMemo(
    () => loadedSkus.flatMap(s => s.products),
    [loadedSkus]
  );

  // Al limpiar la tabla o cambiar de cliente, reset
  useEffect(() => {
    setLoadedSkus([]);
    setPriceHistoryData(null);
    setNotice(null);
  }, [tableName]);

  const handleLoadSkuProducts = (products: any[]) => {
    setNotice(null);
    if (!products || products.length === 0) return;

    const sku: string = products[0]._loadedFromSku;
    if (!sku) return;

    // Dedupe: mismo SKU dos veces
    if (loadedSkus.some(s => s.sku === sku)) {
      setNotice(`El Grupo "${sku}" ya está en la comparación.`);
      return;
    }

    // Máximo 2 SKUs
    if (loadedSkus.length >= MAX_SKUS) {
      setNotice(`Máximo ${MAX_SKUS} Grupos. Quitá uno para agregar otro.`);
      return;
    }

    setLoadedSkus(prev => [...prev, { sku, products }]);
  };

  const handleRemoveSku = (sku: string) => {
    setLoadedSkus(prev => prev.filter(s => s.sku !== sku));
    setNotice(null);
  };

  // Cargar historial de precios (batch) cuando cambian los productos
  const loadPriceHistory = useCallback(async () => {
    if (comparisonProducts.length === 0) {
      setPriceHistoryData(null);
      return;
    }

    setLoadingPriceHistory(true);
    try {
      const productIds = comparisonProducts
        .map(p => p.id_catalogo || p.ID_CATALOGO || p.id)
        .filter((id: any) => id);

      if (productIds.length === 0) {
        setPriceHistoryData(null);
        return;
      }

      const response = await api.post('/api/products/price-history-batch', { productIds });
      const { productHistories, tomas } = response.data;

      setPriceHistoryData({
        productHistories: productHistories || {},
        tomas: tomas || []
      });
    } catch (error) {
      console.error('Error loading SKU price history:', error);
      setPriceHistoryData(null);
    } finally {
      setLoadingPriceHistory(false);
    }
  }, [comparisonProducts]);

  const comparisonProductsKey = comparisonProducts
    .map(p => p.comparisonId || p.id_catalogo || p.ID_CATALOGO || p.id)
    .filter((id: any) => id)
    .sort()
    .join(',');

  useEffect(() => {
    loadPriceHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comparisonProductsKey]);

  return (
    <div className="space-y-4">
      {/* Selector de SKUs */}
      <SkuComparisonLoader
        tableName={tableName}
        onLoadSkuProducts={handleLoadSkuProducts}
      />

      {/* Aviso (dedupe / máximo) */}
      {notice && (
        <div className="flex items-center space-x-2 px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-700">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          <span className="text-sm">{notice}</span>
        </div>
      )}

      {/* SKUs seleccionados (chips removibles) */}
      {loadedSkus.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-gray-600">Comparando:</span>
          {loadedSkus.map(s => (
            <span
              key={s.sku}
              className="inline-flex items-center space-x-2 bg-indigo-100 text-indigo-800 text-sm px-3 py-1 rounded-full"
            >
              <span className="font-medium">{s.sku}</span>
              <span className="text-xs text-indigo-500">
                ({s.products.length} {s.products.length === 1 ? 'producto' : 'productos'})
              </span>
              <button
                onClick={() => handleRemoveSku(s.sku)}
                className="text-indigo-500 hover:text-indigo-800"
                title="Quitar Grupo"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Gráfico de evolución */}
      {comparisonProducts.length > 0 && (
        <PriceTimeline
          comparisonProducts={comparisonProducts}
          shouldLoad={false}
          priceHistoryData={priceHistoryData}
          loading={loadingPriceHistory}
        />
      )}
    </div>
  );
};

export default SkuTrendCompare;
